import { useLogoutAction } from "@/app/hooks/ui_hooks/useLogoutAction";
import { PACKING_ANNOUNCEMENT_MS, usePackingAlerts } from "@/app/hooks/api_hooks/orders/usePackingAlerts";
import { useIsClinicOpsUser } from "@/app/hooks/useIsClinicOpsUser";
import { useRouter } from "@/app/hooks/useRouter";
import useScreenSize from "@/app/hooks/useScreenSize";
import { VMS_CLICK_EVENTS, VMS_SCREEN_IDS, useVMSClickLog } from "@/app/hooks/useVMSClickLog";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { CursorArrowRaysIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import Hamburger from "hamburger-react";
import { startCase } from "lodash";
import { Fragment, memo, useCallback } from "react";
import { HiUserAdd } from "react-icons/hi";
import { IoMdLogOut } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";
import "regenerator-runtime/runtime";
import CommandPalleteComponent from "../../core/CommandPallete";
import MappedClinic from "../../ui/Reusable/MappedClinic";
import PackingAlertsBanner from "../../ui/PackingAlertsBanner";

const CutomerProfilePopover = () => {
  const { getRouteKey } = useRouter();
  const { logVMSClick } = useVMSClickLog(VMS_SCREEN_IDS.HEADER);
  const solutions = [
    {
      name: "Add Customer",
      description: "Add new customer or lead from the website.",
      href: getRouteKey("ADD_CUSTOMER", "url"),
      icon: HiUserAdd,
    },
    {
      name: "Search Customer",
      description: "Search customer and pet details.",
      href: getRouteKey("SEARCH_CUSTOMER", "url"),
      icon: CursorArrowRaysIcon,
    },
  ];

  return (
    <Popover className="relative z-50">
      <Popover.Button
        onClick={() => logVMSClick(VMS_CLICK_EVENTS.HEADER_CUSTOMER_MENU_CLICK, "Customer")}
        className="flex justify-start items-center gap-x-1 hover:bg-gray-700 px-2 py-2 rounded-md w-full font-medium text-gray-600 text-[11px] md:text-xs 2xl:text-sm hover:text-white"
      >
        <span>Customer</span>
        <ChevronDownIcon className="w-5 h-5" aria-hidden="true" />
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className="left-1/2 z-10 absolute flex mt-5 px-4 w-screen max-w-max -translate-x-1/2">
          {({ open, close }) =>
            open && (
              <div className="z-50 flex-auto bg-white shadow-lg rounded-3xl ring-1 ring-gray-900/5 w-screen max-w-md text-sm leading-6 overflow-hidden">
                <div className="p-2 2xl:p-4">
                  {solutions.map((item) => (
                    <div key={item.name} className="relative flex gap-x-6 hover:bg-gray-50 p-2 rounded-lg group">
                      <div className="group-hover:bg-white flex flex-none justify-center items-center bg-gray-50 mt-1 rounded-lg w-11 h-11">
                        <item.icon
                          className="group-hover:text-indigo-600 w-4 h-4 2xl:w-6 2xl:h-6 text-gray-600"
                          aria-hidden="true"
                        />
                      </div>
                      <div>
                        <Link
                          onClick={() => {
                            logVMSClick(VMS_CLICK_EVENTS.HEADER_CUSTOMER_MENU_OPTION_CLICK, item.name);
                            close();
                          }}
                          to={item.href}
                          className="font-semibold text-gray-900 text-xs 2xl:text-sm"
                        >
                          {item.name}
                          <span className="absolute inset-0" />
                        </Link>
                        <p className="mt-1 text-gray-600 text-xs 2xl:text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          }
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};

const Header: React.FC<{
  toggleSidebar: (status: boolean) => void;
  isMirrorViewEnable: boolean;
}> = ({ toggleSidebar, isMirrorViewEnable }) => {
  const { getRouteKey } = useRouter();
  const { isMobile } = useScreenSize();
  const handleLogout = useLogoutAction();
  const { logVMSClick } = useVMSClickLog(VMS_SCREEN_IDS.HEADER);
  const navigate = useNavigate();
  // clinic_ops: while orders are pending packing (or one just arrived) the
  // packing banner replaces Customer / Calendar / user name in the navbar;
  // otherwise those items show. Their Logout lives only in the sidebar.
  const isClinicOpsUser = useIsClinicOpsUser();
  const { pendingCount, announcementCount } = usePackingAlerts({ enabled: !isMirrorViewEnable });
  const showPackingBanner = isClinicOpsUser && (pendingCount > 0 || announcementCount > 0);

  const openConfirmedOrders = useCallback(() => {
    logVMSClick(VMS_CLICK_EVENTS.HEADER_PACKING_ALERT_CLICK, `Pack ASAP (${pendingCount})`);
    // ?status=3 opens the Confirmed tab on the OMS page (see StatsComponent).
    navigate(`${getRouteKey("ORDER_MANAGEMENT", "url")}?status=3`);
  }, [logVMSClick, navigate, getRouteKey, pendingCount]);

  const { show: isDesktopNavShowing } = useAppSelector((state) => state.root.UIStates.sidebar.desktop);
  const user = useAppSelector((state) => state?.auth?.user);
  const userPermissions = useAppSelector((state) => state?.auth?.userPermissions);
  const isDesktopViewport = window.innerWidth >= 1024;

  if (isMirrorViewEnable) return null;

  // Original navbar items (Customer / Calendar / user name / Logout; no Logout for clinic_ops).
  const defaultNavItems = (
    <>
      <div className="hidden min-[1151px]:flex shrink-0">{!isMobile && <CutomerProfilePopover />}</div>

      <Link
        to={getRouteKey("STAFF_CALENDAR", "url")}
        onClick={() => logVMSClick(VMS_CLICK_EVENTS.HEADER_CALENDAR_CLICK, "Calendar")}
        className="hidden min-[1151px]:flex items-center gap-x-1 hover:bg-gray-700 px-2 py-2 rounded-md font-medium text-gray-600 text-[11px] md:text-xs 2xl:text-sm hover:text-white whitespace-nowrap shrink-0"
      >
        Calendar
      </Link>

      <div className="flex flex-nowrap items-center gap-2 min-[1151px]:gap-4 ml-auto shrink-0">
        {/* Separator */}
        <div className="hidden min-[1151px]:block w-px h-6 bg-gray-900/10" aria-hidden="true" />

        <div className="hidden min-[1151px]:flex flex-col items-center">
          <span className="font-semibold text-gray-900 text-sm leading-6">{user?.displayName}</span>
          {userPermissions?.role_name && (
            <span className="text-xs text-gray-500">{startCase(userPermissions.role_name.split("_").join(" "))}</span>
          )}
        </div>

        {!isClinicOpsUser && (
          <button
            onClick={handleLogout}
            className={clsx(
              "flex items-center gap-x-1.5 px-2 py-1 rounded-md font-semibold shrink-0",
              "bg-blue-50 hover:bg-red-100 text-gray-600 leading-5 transition-colors",
            )}
          >
            <IoMdLogOut className="w-4 h-4 text-red-600 shrink-0" />
            <span className="text-[10px] sm:text-xs 2xl:text-sm whitespace-nowrap">Logout</span>
          </button>
        )}
      </div>
    </>
  );

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm shrink-0">
      <div className="flex flex-nowrap items-center gap-2 sm:gap-3 lg:gap-4 h-10 md:h-12 2xl:h-14 px-2 sm:px-3 lg:px-4">
        {(!isDesktopViewport || !isDesktopNavShowing) && (
          <div className="flex items-center shrink-0">
            <Hamburger
              size={20}
              toggled={false}
              color="#111827"
              onToggle={() => {
                logVMSClick(VMS_CLICK_EVENTS.HEADER_HAMBURGER_CLICK, "Open Navigation");
                toggleSidebar(true);
              }}
            />
          </div>
        )}
        {isMobile && <div className="w-px h-6 bg-gray-900/10" aria-hidden="true" />}

        <div className="flex flex-1 flex-nowrap items-center gap-2 lg:gap-3 2xl:gap-6 min-w-0">
          <div className="min-w-0">
            <MappedClinic />
          </div>

          <div className="relative z-30 flex flex-1 justify-start sm:min-w-[260px] items-center min-w-0">
            <CommandPalleteComponent />
          </div>

          {isClinicOpsUser ? (
            <AnimatePresence mode="wait" initial={false}>
              {showPackingBanner ? (
                <motion.div
                  key="packing-banner"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center ml-auto min-w-0 w-[340px] lg:w-[400px] 2xl:w-[480px] max-w-full"
                >
                  <PackingAlertsBanner
                    pendingCount={pendingCount}
                    announcementCount={announcementCount}
                    announcementDurationMs={PACKING_ANNOUNCEMENT_MS}
                    onClick={openConfirmedOrders}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="default-nav-items"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex flex-nowrap items-center gap-2 lg:gap-3 2xl:gap-6 ml-auto shrink-0"
                >
                  {defaultNavItems}
                </motion.div>
              )}
            </AnimatePresence>
          ) : (
            defaultNavItems
          )}
        </div>
      </div>
    </header>
  );
};

export default memo(Header);
